<script setup lang="ts">
import AgendaView from '@/views/apps/agenda/AgendaView.vue'
import type { AgendaCommittee } from '@/views/apps/agenda/types'
import {
  fetchEligibleCommittees,
  fetchNextFutureMeetingDate,
} from '@/views/apps/agenda/useAgenda'
import PageWithTableSkeleton from '@/views/components/skeleton/PageWithTableSkeleton.vue'

definePage({
  meta: {
    action: '1',
    subject: '4',
  },
})

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)
const committees = ref<AgendaCommittee[]>([])
const initialDate = ref<string | null>(null)

const resolveDirectLinkCommitteeId = (): string | null => {
  const committeeParam = route.query.committee

  return typeof committeeParam === 'string' ? committeeParam : null
}

const applyDirectLink = (
  committeeList: AgendaCommittee[],
  committeeId: string,
): AgendaCommittee[] => {
  return committeeList.map(committee => ({
    ...committee,
    selected: committee.id === committeeId,
  }))
}

const handleDirectLink = async (
  committeeList: AgendaCommittee[],
): Promise<AgendaCommittee[]> => {
  const committeeId = resolveDirectLinkCommitteeId()

  if (!committeeId)
    return committeeList

  router.replace({ query: {} })

  const exists = committeeList.some(committee => committee.id === committeeId)

  if (!exists)
    return committeeList

  initialDate.value = await fetchNextFutureMeetingDate(committeeId)

  return applyDirectLink(committeeList, committeeId)
}

onMounted(async () => {
  try {
    const eligible = await fetchEligibleCommittees()

    committees.value = await handleDirectLink(eligible)
  }
  finally {
    isLoading.value = false
  }
})
</script>

<template>
  <div class="agenda-page">
    <PageWithTableSkeleton v-if="isLoading" />

    <AgendaView
      v-else
      :committees="committees"
      :initial-date="initialDate"
    />
  </div>
</template>

<style lang="scss">
.layout-content-width-boxed .layout-page-content:has(.agenda-page) {
  max-inline-size: none;
  padding-block: 1.625rem 1.625rem;
  padding-inline: 1.625rem;
  margin-block-start: 0;
}
</style>
